/* Parse short Bible references written in Portuguese.
 *
 * Handles:
 *   "Jo 3:16"         → { bookSlug: "joao", chapter: 3, verse: 16 }
 *   "João 3:16"       → idem
 *   "Salmos 23"       → { bookSlug: "salmos", chapter: 23 }
 *   "Sl 23"           → idem
 *   "Romanos 8:28"    → { bookSlug: "romanos", chapter: 8, verse: 28 }
 *   "2Co 4:7"         → { bookSlug: "2corintios", chapter: 4, verse: 7 }
 *   "1 Cor 13"        → { bookSlug: "1corintios", chapter: 13 }
 *
 * Returns `null` when the string is not a recognizable reference.
 */

import { BIBLE_BOOKS } from "../data/bibleBooks";

// ---- Build an alias dictionary once ----
const stripAccents = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const ALIASES = {};
const registerAlias = (key, book) => {
  const k = stripAccents(key).replace(/\s+/g, "");
  if (k) ALIASES[k] = book.slug;
};

for (const b of BIBLE_BOOKS) {
  registerAlias(b.name, b);
  registerAlias(b.abbr, b);
  registerAlias(b.slug, b);
  registerAlias(b.apiSlug, b);
}

// Extra common abbreviations people type
const EXTRA = {
  sl: "salmos", slm: "salmos", slms: "salmos",
  pv: "proverbios", prv: "proverbios",
  ec: "eclesiastes", ecl: "eclesiastes",
  ct: "cantares",
  gn: "genesis", gen: "genesis",
  ex: "exodo", exo: "exodo",
  lv: "levitico", lev: "levitico",
  nm: "numeros", num: "numeros",
  dt: "deuteronomio", deut: "deuteronomio",
  js: "josue",
  jz: "juizes",
  rt: "rute",
  "1sm": "1samuel", "1sam": "1samuel",
  "2sm": "2samuel", "2sam": "2samuel",
  "1rs": "1reis",
  "2rs": "2reis",
  "1cr": "1cronicas", "1cron": "1cronicas",
  "2cr": "2cronicas", "2cron": "2cronicas",
  ed: "esdras",
  ne: "neemias",
  et: "ester",
  is: "isaias",
  jr: "jeremias",
  lm: "lamentacoes", lam: "lamentacoes",
  ez: "ezequiel",
  dn: "daniel",
  os: "oseias",
  jl: "joel",
  am: "amos",
  ob: "obadias",
  jn: "jonas", jon: "jonas",
  mq: "miqueias",
  na: "naum",
  hc: "habacuque",
  sf: "sofonias",
  ag: "ageu",
  zc: "zacarias",
  ml: "malaquias",
  mt: "mateus",
  mc: "marcos",
  lc: "lucas",
  jo: "joao", joa: "joao",
  at: "atos",
  rm: "romanos", rom: "romanos",
  "1co": "1corintios", "1cor": "1corintios",
  "2co": "2corintios", "2cor": "2corintios",
  gl: "galatas", gal: "galatas",
  ef: "efesios", efe: "efesios",
  fp: "filipenses", fil: "filipenses",
  cl: "colossenses", col: "colossenses",
  "1ts": "1tessalonicenses", "1tess": "1tessalonicenses",
  "2ts": "2tessalonicenses", "2tess": "2tessalonicenses",
  "1tm": "1timoteo", "1tim": "1timoteo",
  "2tm": "2timoteo", "2tim": "2timoteo",
  tt: "tito",
  fm: "filemom", flm: "filemom",
  hb: "hebreus", heb: "hebreus",
  tg: "tiago",
  "1pe": "1pedro", "1ped": "1pedro",
  "2pe": "2pedro", "2ped": "2pedro",
  "1jo": "1joao",
  "2jo": "2joao",
  "3jo": "3joao",
  jd: "judas",
  ap: "apocalipse", apoc: "apocalipse",
};

Object.assign(ALIASES, EXTRA);

/** Best-effort parser. Returns { bookSlug, chapter, verse? } or null. */
export function parseReference(raw) {
  if (!raw || typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text) return null;

  // Pattern: optional leading number, book name, chapter, optional :verse
  // Examples captured:  "1Co 13:4",  "1 Coríntios 13",  "salmos 23:1",  "Jo3:16"
  const m = text.match(
    /^\s*([123]?\s*[A-Za-zÀ-ÖØ-öø-ÿ\u00c0-\u017f\.]+)\s*(\d+)(?:\s*[:.,\s]\s*(\d+))?\s*$/
  );
  if (!m) return null;

  const rawBook = m[1].replace(/\./g, "");
  const key = stripAccents(rawBook).replace(/\s+/g, "");
  const bookSlug = ALIASES[key];
  if (!bookSlug) return null;

  const chapter = Number.parseInt(m[2], 10);
  if (!chapter || chapter < 1) return null;

  const verse = m[3] ? Number.parseInt(m[3], 10) : null;
  return { bookSlug, chapter, verse: verse && verse > 0 ? verse : null };
}

export function buildReferenceHref(ref) {
  if (!ref || !ref.bookSlug || !ref.chapter) return null;
  const base = `/biblia/${ref.bookSlug}/${ref.chapter}`;
  return ref.verse ? `${base}?v=${ref.verse}` : base;
}
